
import { useState } from 'react'
import { UserPlus, Users } from 'lucide-react'
import { Body, Button, ButtonVariant } from '@meiuca/ui-components'
import { UserSearchInput } from './UserSearchInput'
import { UserSearchResult } from '@/services/userService'
import { GuestInviteModal } from './GuestInviteModal'
import { Game } from '@/types/game'
import { canInvite, isGameFull } from '@/utils/gameUtils'

interface InviteUserSectionProps {
  game: Game
  isAdmin: boolean
  onInviteUser?: (gameId: string, userId: string) => Promise<void>
  onInviteGuest?: (gameId: string, name: string, phoneNumber?: string) => Promise<void>
  isActionLoading?: string | null
}

export function InviteUserSection({ game, isAdmin, onInviteUser, onInviteGuest, isActionLoading }: InviteUserSectionProps) {
  const [isInviting, setIsInviting] = useState(false)
  const [showGuestInviteModal, setShowGuestInviteModal] = useState(false)

  if (!isAdmin) {
    return null
  }

  // Usa helpers centralizados para lógica de jogo
  const gameIsFull = isGameFull(game)
  const invitesAllowed = canInvite(game)

  const handleUserSelect = async (user: UserSearchResult) => {
    try {
      setIsInviting(true)
      await onInviteUser?.(game.id, user.id)
    } catch (error) {
      console.error('Error inviting user:', error)
      // Error is handled by the parent component
    } finally {
      setIsInviting(false)
    }
  }

  const handleGuestInvite = async (name: string, phoneNumber?: string) => {
    if (onInviteGuest) {
      await onInviteGuest(game.id, name, phoneNumber)
    }
  }

  return (
    <>
      <div className="mb-6 pb-4 border-b border-border-primary">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Users className="w-4 h-4 text-brand-primary" />
            <Body className="text-sm font-medium text-text-primary">Convidar Pessoas</Body>
          </div>
          
          {onInviteGuest && (
            <Button
              variant={ButtonVariant.PRIMARY}
              buttonStyle="clear"
              size="sm"
              onClick={() => setShowGuestInviteModal(true)}
              loading={isActionLoading === 'invite-guest'}
              disabled={!invitesAllowed}
              title={gameIsFull ? "Jogo lotado" : "Convidar pessoa sem conta"}
            >
              <UserPlus className="w-4 h-4" />
              {gameIsFull ? "Jogo Lotado" : "Convidar Convidado"}
            </Button>
          )}
        </div>
        
        {onInviteUser && (
          <div className="space-y-2">
            <Body className="text-xs text-text-secondary font-medium">USUÁRIOS CADASTRADOS</Body>
            <UserSearchInput
              onUserSelect={handleUserSelect}
              placeholder={gameIsFull ? "Jogo lotado" : isInviting ? "Enviando convite..." : "Busque por nome, nickname ou telefone..."}
              className="w-full"
              disabled={isInviting || !invitesAllowed}
            />
            {gameIsFull && (
              <Body className="text-xs text-text-secondary">
                Jogo lotado ({game.occupiedSlots}/{game.totalSlots} participantes)
              </Body>
            )}
          </div>
        )}
      </div>

      {/* Guest Invite Modal */}
      <GuestInviteModal 
        open={showGuestInviteModal}
        onClose={() => setShowGuestInviteModal(false)}
        onInviteGuest={handleGuestInvite}
        isLoading={isActionLoading === 'invite-guest'}
      />
    </>
  )
}